import React from 'react';
import { Route, Navigate } from 'react-router-dom';
import ProtectedRoute from '@/modules/auth/ProtectedRoute';
import PortalGatekeeper from '@/modules/common/components/PortalGatekeeper';

const G = ({ children }) => (
  <PortalGatekeeper roleName="teacher" portalDashboardPath="/docente/dashboard">
    {children}
  </PortalGatekeeper>
);

// Lazy-loaded tab containers
const ClasesContenidosPage = React.lazy(() => import('@/modules/teacher/clases-contenidos/ClasesContenidosPage'));
const ActividadesDocentePage = React.lazy(() => import('@/modules/teacher/actividades/ActividadesDocentePage'));
const EvaluacionDocentePage = React.lazy(() => import('@/modules/teacher/evaluacion/EvaluacionDocentePage'));
const SeguimientoDocentePage = React.lazy(() => import('@/modules/teacher/seguimiento/SeguimientoDocentePage'));
const FinanzasDocentePage = React.lazy(() => import('@/modules/teacher/finanzas/FinanzasDocentePage'));

// Dashboard & Profile (single pages)
const DocenteDashboard = React.lazy(() => import('@/pages/dashboards/DocenteDashboard'));
const MiPerfilDocentePage = React.lazy(() => import('@/modules/teacher/perfil/MiPerfilDocentePage'));

// LMS detail routes (outside tabs)
const KnowledgeBasesList = React.lazy(() => import('@/modules/lms/components/teacher/KnowledgeBasesList'));
const SessionsList = React.lazy(() => import('@/modules/lms/components/teacher/SessionsList'));
const SessionProgressPage = React.lazy(() => import('@/modules/lms/components/teacher/SessionProgressPage'));
const SessionContentPage = React.lazy(() => import('@/modules/lms/components/teacher/SessionContentPage'));
const TeacherAnalyticsDashboard = React.lazy(() => import('@/modules/lms/components/teacher/TeacherAnalyticsDashboard'));
const CilDashboard = React.lazy(() => import('@/modules/lms/components/teacher/CilDashboard'));

export const teacherRoutes = [
  // Dashboard & Profile (single pages)
  <Route key="/docente/dashboard" path="/docente/dashboard" element={
    <ProtectedRoute requiredPermission="teacher.portal.view"><G><DocenteDashboard /></G></ProtectedRoute>
  } />,
  <Route key="/docente/perfil" path="/docente/perfil" element={
    <ProtectedRoute requiredPermission="teacher.portal.view"><G><MiPerfilDocentePage /></G></ProtectedRoute>
  } />,

  // Clases y Contenidos (2 niveles + tabs) — SPLIT from Aula Virtual Docente
  <Route key="/docente/clases-contenidos" path="/docente/clases-contenidos" element={
    <Navigate to="/docente/clases-contenidos/contenidos" replace />
  } />,
  <Route key="/docente/clases-contenidos/contenidos" path="/docente/clases-contenidos/contenidos" element={
    <ProtectedRoute requiredPermission="teacher.portal.view"><G><ClasesContenidosPage /></G></ProtectedRoute>
  } />,
  <Route key="/docente/clases-contenidos/clases-en-vivo" path="/docente/clases-contenidos/clases-en-vivo" element={
    <ProtectedRoute requiredPermission="teacher.portal.view"><G><ClasesContenidosPage /></G></ProtectedRoute>
  } />,
  <Route key="/docente/clases-contenidos/grabaciones" path="/docente/clases-contenidos/grabaciones" element={
    <ProtectedRoute requiredPermission="teacher.portal.view"><G><ClasesContenidosPage /></G></ProtectedRoute>
  } />,

  // Actividades (2 niveles + tabs) — SPLIT from Aula Virtual Docente
  <Route key="/docente/actividades" path="/docente/actividades" element={
    <Navigate to="/docente/actividades/tareas" replace />
  } />,
  <Route key="/docente/actividades/tareas" path="/docente/actividades/tareas" element={
    <ProtectedRoute requiredPermission="teacher.portal.view"><G><ActividadesDocentePage /></G></ProtectedRoute>
  } />,
  <Route key="/docente/actividades/foros" path="/docente/actividades/foros" element={
    <ProtectedRoute requiredPermission="teacher.portal.view"><G><ActividadesDocentePage /></G></ProtectedRoute>
  } />,
  <Route key="/docente/actividades/tutorias" path="/docente/actividades/tutorias" element={
    <ProtectedRoute requiredPermission="teacher.portal.view"><G><ActividadesDocentePage /></G></ProtectedRoute>
  } />,
  // LMS detail routes — viven fuera del tab container
  <Route key="/docente/actividades/tutorias/bases-conocimiento" path="/docente/actividades/tutorias/bases-conocimiento" element={
    <G><KnowledgeBasesList /></G>
  } />,
  <Route key="/docente/actividades/tutorias/sesiones" path="/docente/actividades/tutorias/sesiones" element={
    <G><SessionsList /></G>
  } />,
  <Route key="/docente/actividades/tutorias/sesiones/:sessionId/progreso" path="/docente/actividades/tutorias/sesiones/:sessionId/progreso" element={
    <G><SessionProgressPage /></G>
  } />,
  <Route key="/docente/actividades/tutorias/sesiones/:sessionId/contenido" path="/docente/actividades/tutorias/sesiones/:sessionId/contenido" element={
    <G><SessionContentPage /></G>
  } />,
  <Route key="/docente/actividades/tutorias/analytics" path="/docente/actividades/tutorias/analytics" element={
    <G><TeacherAnalyticsDashboard /></G>
  } />,
  <Route key="/docente/actividades/tutorias/cil" path="/docente/actividades/tutorias/cil" element={
    <G><CilDashboard /></G>
  } />,

  // Evaluación (2 niveles + tabs)
  <Route key="/docente/evaluacion" path="/docente/evaluacion" element={
    <Navigate to="/docente/evaluacion/calificaciones" replace />
  } />,
  <Route key="/docente/evaluacion/calificaciones" path="/docente/evaluacion/calificaciones" element={
    <ProtectedRoute requiredPermission="teacher.portal.view"><G><EvaluacionDocentePage /></G></ProtectedRoute>
  } />,
  <Route key="/docente/evaluacion/rubricas" path="/docente/evaluacion/rubricas" element={
    <ProtectedRoute requiredPermission="teacher.portal.view"><G><EvaluacionDocentePage /></G></ProtectedRoute>
  } />,
  <Route key="/docente/evaluacion/evaluaciones-linea" path="/docente/evaluacion/evaluaciones-linea" element={
    <ProtectedRoute requiredPermission="teacher.portal.view"><G><EvaluacionDocentePage /></G></ProtectedRoute>
  } />,
  <Route key="/docente/evaluacion/actas" path="/docente/evaluacion/actas" element={
    <ProtectedRoute requiredPermission="teacher.portal.view"><G><EvaluacionDocentePage /></G></ProtectedRoute>
  } />,

  // Seguimiento (2 niveles + tabs)
  <Route key="/docente/seguimiento" path="/docente/seguimiento" element={
    <Navigate to="/docente/seguimiento/asistencias" replace />
  } />,
  <Route key="/docente/seguimiento/asistencias" path="/docente/seguimiento/asistencias" element={
    <ProtectedRoute requiredPermission="teacher.portal.view"><G><SeguimientoDocentePage /></G></ProtectedRoute>
  } />,
  <Route key="/docente/seguimiento/incidencias" path="/docente/seguimiento/incidencias" element={
    <ProtectedRoute requiredPermission="teacher.portal.view"><G><SeguimientoDocentePage /></G></ProtectedRoute>
  } />,
  <Route key="/docente/seguimiento/alumnos-riesgo" path="/docente/seguimiento/alumnos-riesgo" element={
    <ProtectedRoute requiredPermission="teacher.portal.view"><G><SeguimientoDocentePage /></G></ProtectedRoute>
  } />,
  <Route key="/docente/seguimiento/reportes-avance" path="/docente/seguimiento/reportes-avance" element={
    <ProtectedRoute requiredPermission="teacher.portal.view"><G><SeguimientoDocentePage /></G></ProtectedRoute>
  } />,

  // Finanzas (2 niveles + tabs)
  <Route key="/docente/finanzas" path="/docente/finanzas" element={
    <Navigate to="/docente/finanzas/estado-cuenta" replace />
  } />,
  <Route key="/docente/finanzas/estado-cuenta" path="/docente/finanzas/estado-cuenta" element={
    <ProtectedRoute requiredPermission="teacher.portal.view"><G><FinanzasDocentePage /></G></ProtectedRoute>
  } />,
  <Route key="/docente/finanzas/timbrados" path="/docente/finanzas/timbrados" element={
    <ProtectedRoute requiredPermission="teacher.portal.view"><G><FinanzasDocentePage /></G></ProtectedRoute>
  } />,

  // Legacy redirects from old Aula Virtual Docente paths
  <Route key="/docente/aula-virtual/contenidos" path="/docente/aula-virtual/contenidos" element={<Navigate to="/docente/clases-contenidos/contenidos" replace />} />,
  <Route key="/docente/aula-virtual/clases-en-vivo" path="/docente/aula-virtual/clases-en-vivo" element={<Navigate to="/docente/clases-contenidos/clases-en-vivo" replace />} />,
  <Route key="/docente/aula-virtual/grabaciones" path="/docente/aula-virtual/grabaciones" element={<Navigate to="/docente/clases-contenidos/grabaciones" replace />} />,
  <Route key="/docente/aula-virtual/tareas" path="/docente/aula-virtual/tareas" element={<Navigate to="/docente/actividades/tareas" replace />} />,
  <Route key="/docente/aula-virtual/foros" path="/docente/aula-virtual/foros" element={<Navigate to="/docente/actividades/foros" replace />} />,
  <Route key="/docente/aula-virtual/tutorias" path="/docente/aula-virtual/tutorias" element={<Navigate to="/docente/actividades/tutorias" replace />} />,
  <Route key="/docente/aula-virtual/tutorias/bases-conocimiento" path="/docente/aula-virtual/tutorias/bases-conocimiento" element={<KnowledgeBasesList />} />,
  <Route key="/docente/aula-virtual/tutorias/sesiones" path="/docente/aula-virtual/tutorias/sesiones" element={<SessionsList />} />,
  <Route key="/docente/aula-virtual/tutorias/sesiones/:sessionId/progreso" path="/docente/aula-virtual/tutorias/sesiones/:sessionId/progreso" element={<SessionProgressPage />} />,
  <Route key="/docente/aula-virtual/tutorias/sesiones/:sessionId/contenido" path="/docente/aula-virtual/tutorias/sesiones/:sessionId/contenido" element={<SessionContentPage />} />,
  <Route key="/docente/aula-virtual/tutorias/analytics" path="/docente/aula-virtual/tutorias/analytics" element={<TeacherAnalyticsDashboard />} />,
];
